"use client";

import type React from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { usePathname, useSearchParams } from "next/navigation";

import { datadogRum, type RumInitConfiguration } from "@datadog/browser-rum";

import { useAuthUser } from "@/context/AuthUserContext";
import { redactRumEvent } from "@/utils/redactSignedTileUrl";

// Types
interface RUMEvent {
  type: string;
  date: number;
  service?: string;
  view?: {
    name?: string;
    url?: string;
  };
  context?: Record<string, unknown>;
  /**
   * Present on `resource` events. Declared here (rather than reached through
   * a cast) so the redaction in `beforeSend` is type-checked against the
   * field it actually mutates.
   */
  resource?: {
    url?: string;
  };
  /** Present on `error` events raised by a failed request. */
  error?: {
    message?: string;
    stack?: string;
    resource?: {
      url?: string;
    };
  };
}

interface DatadogContext {
  [key: string]: string | number | boolean | null | undefined;
}

interface DatadogRUMWindow {
  addError(
    message: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    options: { context?: Record<string, any>; tags?: Record<string, string> },
  ): void;
}

// Type guard to check if DD_RUM exists on the window object
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isDatadogRUMAvailableWindow = (obj: any): obj is DatadogRUMWindow => {
  return obj && typeof obj.addError === "function";
};

declare global {
  interface Window {
    DD_RUM?: DatadogRUMWindow | null;
  }
}

interface DatadogConfig {
  applicationId: string;
  clientToken: string;
  env?: string;
  service?: string;
  site?: string;
  version?: string;
  allowedTracingUrls: RumInitConfiguration["allowedTracingUrls"];
  sampleRates?: {
    session?: number;
    replay?: number;
  };
}

interface DatadogError extends Error {
  code?: string;
  context?: DatadogContext;
}

interface DatadogFeatureFlags {
  enableReplay: boolean;
  enableErrorTracking: boolean;
  enablePerformanceTracking: boolean;
}

interface DatadogProviderProps {
  children: React.ReactNode;
  organizationId: string;
  config?: Partial<DatadogConfig>;
  debug?: boolean;
  features?: Partial<DatadogFeatureFlags>;
}

// Constants
const CONFIG_DEFAULTS = {
  service: "mailtech-frontend",
  site: "us5.datadoghq.com",
  version: "0.0.0",
  sampleRates: {
    session: 100,
    replay: 100,
  },
} as const;

const DEFAULT_FEATURES: DatadogFeatureFlags = {
  enableReplay: true,
  enableErrorTracking: true,
  enablePerformanceTracking: true,
};

const RETRY_DELAY = 1000;
const MAX_RETRY_ATTEMPTS = 3;

const isDevelopment =
  process.env.NEXT_PUBLIC_APP_ENV === "development" ||
  process.env.NEXT_PUBLIC_APP_ENV === "staging";
const isProduction = process.env.NEXT_PUBLIC_APP_ENV === "production";

const BACKEND_TRACING_URLS = [
  process.env.NEXT_PUBLIC_MAILTECH_API,
  process.env.NEXT_PUBLIC_CONSTELLATION_API_URL,
  process.env.NEXT_PUBLIC_OMNEURAL_MAIL_API,
].filter((url): url is string => Boolean(url));

// Called from init (client-side only) — window is available there.
const getAllowedTracingUrls =
  (): RumInitConfiguration["allowedTracingUrls"] => [
    // Same-origin server-action POSTs and RSC fetches — the Next.js server
    // (@vercel/otel, see src/instrumentation.ts) extracts W3C tracecontext, so
    // the server trace becomes a child of the RUM-generated trace.
    { match: window.location.origin, propagatorTypes: ["tracecontext"] },
    // Direct browser→backend calls — dd-trace extracts either style.
    ...BACKEND_TRACING_URLS.map((url) => ({
      match: url,
      propagatorTypes: ["datadog", "tracecontext"] satisfies (
        | "datadog"
        | "tracecontext"
      )[],
    })),
  ];

const getEnvironmentConfig = () => ({
  trackUserInteractions: isProduction || isDevelopment,
  trackResources: isProduction || isDevelopment,
  trackLongTasks: isProduction || isDevelopment,
  debug: isDevelopment,
});

const validateConfig = (config: Partial<DatadogConfig>): boolean => {
  if (!config?.applicationId) {
    console.warn("Datadog RUM: Missing applicationId");
    return false;
  }
  if (!config?.clientToken) {
    console.warn("Datadog RUM: Missing clientToken");
    return false;
  }
  return true;
};

const getConfig = (userConfig: Partial<DatadogConfig>): DatadogConfig => ({
  ...CONFIG_DEFAULTS,
  ...userConfig,
  applicationId:
    userConfig.applicationId ?? process.env.NEXT_PUBLIC_DD_APPLICATION_ID!,
  clientToken:
    userConfig.clientToken ?? process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN!,
  env: process.env.NEXT_PUBLIC_APP_ENV,
  version: userConfig.version ?? process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA!,
  sampleRates: {
    ...CONFIG_DEFAULTS.sampleRates,
    ...userConfig.sampleRates,
  },
  allowedTracingUrls: userConfig.allowedTracingUrls ?? getAllowedTracingUrls(),
});

const sanitizeContext = (context: unknown): DatadogContext => {
  if (typeof context !== "object" || !context) return {};

  return Object.entries(context).reduce((acc, [key, value]) => {
    if (["string", "number", "boolean"].includes(typeof value)) {
      acc[key] = value;
    }
    return acc;
  }, {} as DatadogContext);
};

const handleDatadogError = (error: DatadogError, operation: string) => {
  if (isDevelopment) {
    console.error(`Datadog RUM [${operation}]:`, {
      message: error.message,
      code: error.code,
      context: error.context,
    });
  } else if (isProduction) {
    if (isDatadogRUMAvailableWindow(window.DD_RUM)) {
      window.DD_RUM?.addError(error.message, {
        context: {
          code: error.code,
          operation,
          ...error.context,
        },
        tags: {
          env: "production",
          severity: "error",
        },
      });
    } else {
      console.error("Error in Error tracking....");
    }
  }
};

const isDatadogEnabled = (): boolean => {
  if (typeof window === "undefined") return false;
  return (
    typeof datadogRum !== "undefined" &&
    window.location.origin.includes("mailtech.io")
  );
};

/**
 * Provider component for Datadog RUM integration
 * @param {DatadogProviderProps} props - Configuration props
 */

export function DatadogProvider({
  children,
  organizationId,
  config = {},
  debug = false,
  features = DEFAULT_FEATURES,
}: DatadogProviderProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const user = useAuthUser();
  const initialized = useRef(false);
  const initError = useRef<Error | null>(null);

  const searchParamsString = useMemo(
    () => searchParams.toString(),
    [searchParams],
  );

  const initializeWithRetry = useCallback(
    async (
      initConfig: Parameters<typeof datadogRum.init>[0],
      attempts = MAX_RETRY_ATTEMPTS,
    ) => {
      try {
        datadogRum.init(initConfig);
        initialized.current = true;
        initError.current = null;
      } catch (error) {
        handleDatadogError(error as DatadogError, "initialization");
        initError.current = error as Error;

        if (attempts > 0) {
          setTimeout(
            () => initializeWithRetry(initConfig, attempts - 1),
            RETRY_DELAY,
          );
        }
      }
    },
    [],
  );

  // Initialize RUM once with combined availability check
  useEffect(() => {
    if (initialized.current || !isDatadogEnabled()) return;

    const finalConfig = getConfig(config);
    if (!validateConfig(finalConfig)) return;

    if (debug && isDevelopment) {
      console.debug("Datadog RUM Configuration:", {
        env: finalConfig.env,
        service: finalConfig.service,
        features,
      });
    }

    const initConfig = {
      ...finalConfig,
      ...getEnvironmentConfig(),
      sessionSampleRate: finalConfig.sampleRates?.session,
      sessionReplaySampleRate: features.enableReplay
        ? finalConfig.sampleRates?.replay
        : 0,
      trackUserInteractions: features.enablePerformanceTracking,
      trackResources: features.enablePerformanceTracking,
      trackLongTasks: features.enablePerformanceTracking,
      // End-to-end head sampling knob: % of browser-initiated requests that
      // start a distributed trace. Revisit after measuring drain volume.
      traceSampleRate: 100,
      // Only inject trace headers when sampled — Vercel drops server spans
      // for requests arriving with an explicit sampled=0.
      traceContextInjection: "sampled" as const,
      defaultPrivacyLevel: "mask-user-input" as const,
      // First-party relay so ad blockers can't drop intake traffic (they
      // block browser-intake-*-datadoghq.com, and the string form of this
      // option is fingerprinted via its ?ddforward= parameter — hence the
      // function form with an opaque payload). The relay responds 200 and
      // forwards in the background, so the perf cost that got the proxy
      // disabled in #331 no longer applies (see /api/relay).
      proxy: (options: { path: string; parameters: string }) =>
        `${window.location.origin}/api/relay?b=${encodeURIComponent(
          btoa(`${options.path}?${options.parameters}`),
        )}`,
      beforeSend: (event: RUMEvent) => {
        event.context = sanitizeContext(event.context);
        // Signed URLs are fetched straight from the browser — the pressure
        // layer's CloudFront tiles and manifest, and the place-CSV upload's
        // S3 pre-signed PUT — so every one of those fetches is a `resource`
        // event whose URL carries the signature itself. Strip it before the
        // event leaves the browser, otherwise the credential is what gets
        // shipped to Datadog and retained there. Detection is by the shape of
        // the value, so ordinary URLs are returned untouched. `false` DROPS
        // the event: the SDK discards beforeSend edits to `error.causes`, so
        // a signed parameter there can only be withheld wholesale. On the
        // Constellation/Cosmic origins every query VALUE is also replaced
        // (names kept): those carry customer search terms and filters.
        return redactRumEvent(event);
      },
    };

    initializeWithRetry(initConfig);

    return () => {
      if (isDatadogEnabled()) {
        datadogRum.clearGlobalContext();
      }
    };
  }, [config, debug, features, initializeWithRetry]);

  // Handle user session
  useEffect(() => {
    if (!isDatadogEnabled() || !initialized.current) return;

    try {
      const currentUser = datadogRum.getUser();

      if (
        !currentUser ||
        currentUser.id !== user.id ||
        currentUser.organization_id !== organizationId
      ) {
        datadogRum.setUser({
          id: user.id,
          name: `${user.first_name} ${user.last_name}`,
          email: user.email,
          organization_id: organizationId,
        });
      }
    } catch (error) {
      handleDatadogError(error as DatadogError, "user-session");
    }
    return () => datadogRum.clearUser();
  }, [organizationId, user]);

  // Handle route changes
  useEffect(() => {
    if (!pathname || !isDatadogEnabled() || !initialized.current) return;

    const controller = new AbortController();

    try {
      const context = sanitizeContext({
        route: pathname,
        query: searchParamsString,
      });

      datadogRum.startView({
        name: pathname,
        context,
      });
    } catch (error) {
      handleDatadogError(error as DatadogError, "route-change");
    }

    return () => {
      controller.abort();
    };
  }, [pathname, searchParamsString]);

  return children;
}

// Helper functions for manual tracking
// Note: These can only be used in client components

export const DatadogRUM = {
  addError: (error: Error, context?: unknown) => {
    if (isDatadogEnabled()) {
      datadogRum.addError(error, sanitizeContext(context));
    }
  },

  addAction: (name: string, context?: unknown) => {
    if (isDatadogEnabled()) {
      datadogRum.addAction(name, sanitizeContext(context));
    }
  },

  setContext: (context: unknown) => {
    if (isDatadogEnabled()) {
      datadogRum.setGlobalContext(sanitizeContext(context));
    }
  },

  startView: (name: string, context?: unknown) => {
    if (isDatadogEnabled()) {
      datadogRum.startView({
        name,
        context: sanitizeContext(context),
      });
    }
  },
};

/**
 *
 * Using Helper Functions:
 *
 * Track Errors:
 * ```tsx
 * import { DatadogRUM } from './DatadogProvider';
 *
 * try {
 *   // Your code that might throw
 *   await fetchUserData();
 * } catch (error) {
 *   DatadogRUM.addError(error, {
 *     errorType: 'api_error',
 *     endpoint: '/api/users',
 *     userId: user.id
 *   });
 * }
 * ```
 *
 * Track User Actions:
 * ```tsx
 * function SubmitButton() {
 *   const handleClick = () => {
 *     DatadogRUM.addAction('form_submit', {
 *       formType: 'registration',
 *       userType: 'new'
 *     });
 *   };
 *
 *   return <button onClick={handleClick}>Submit</button>;
 * }
 * ```
 *
 * Set Global Context:
 * ```tsx
 * // Add user preferences to global context
 * DatadogRUM.setContext({
 *   theme: userPreferences.theme,
 *   language: userPreferences.language,
 *   subscriptionTier: user.tier
 * });
 * ```
 *
 * Manual View Tracking:
 * ```tsx
 * // Track virtual page views (e.g., in a SPA)
 * function VirtualPage() {
 *   useEffect(() => {
 *     DatadogRUM.startView('CustomerDashboard', {
 *       section: 'analytics',
 *       customerId: customer.id
 *     });
 *   }, []);
 *
 *   return <Dashboard />;
 * }
 * ```
 *
 * 3. Custom Error Boundary Integration:
 * ```tsx
 * class DatadogErrorBoundary extends React.Component {
 *   componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
 *     DatadogRUM.addError(error, {
 *       errorInfo: errorInfo,
 *       component: this.constructor.name
 *     });
 *   }
 *
 *   render() {
 *     return this.props.children;
 *   }
 * }
 * ```
 */
